import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import {
  completePasswordUpdate,
  establishRecoverySession,
  parseAuthEmailCallback,
} from '../../lib/authCallback';
import { AuthCallbackProgress, AuthCallbackShell } from './AuthCallbackShell';
import { AuthReturnActions } from './AuthReturnActions';

const VERIFY_WATCHDOG_MS = 25_000;

type ResetView =
  | { status: 'verifying' }
  | { status: 'ready'; formError: string | null }
  | { status: 'saving' }
  | { status: 'success' }
  | { status: 'error'; message: string };

/**
 * Browser-only password reset — finite states, no auto app bounce.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<ResetView>({ status: 'verifying' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let settled = false;
    const watchdog = window.setTimeout(() => {
      if (!settled) {
        setView({
          status: 'error',
          message:
            'This is taking too long. The reset link may be expired or your connection dropped. Return to MovieMe and request a fresh password reset.',
        });
      }
    }, VERIFY_WATCHDOG_MS);

    (async () => {
      const params = parseAuthEmailCallback();
      const result = await establishRecoverySession(params);
      settled = true;
      window.clearTimeout(watchdog);

      if (result.ok) {
        setView({ status: 'ready', formError: null });
      } else {
        setView({
          status: 'error',
          message:
            result.message ||
            'This password reset link is invalid or has expired. Return to MovieMe, choose Forgot password, and request a fresh link.',
        });
      }
    })();

    return () => {
      window.clearTimeout(watchdog);
    };
  }, []);

  const goToWebsiteSignIn = () => {
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (view.status !== 'ready') return;

    if (password !== confirmPassword) {
      setView({ status: 'ready', formError: 'Passwords do not match' });
      return;
    }

    if (password.length < 6) {
      setView({ status: 'ready', formError: 'Password must be at least 6 characters' });
      return;
    }

    setView({ status: 'saving' });
    const result = await completePasswordUpdate(password);
    if (result.ok) {
      setView({ status: 'success' });
    } else {
      setView({ status: 'ready', formError: result.message });
    }
  };

  if (view.status === 'verifying') {
    return (
      <AuthCallbackShell liveStatus="Checking your reset link">
        <AuthCallbackProgress label="Checking your reset link…" />
      </AuthCallbackShell>
    );
  }

  if (view.status === 'error') {
    return (
      <AuthCallbackShell liveStatus="Reset link failed">
        <div className="space-y-6 text-center">
          <XCircle className="mx-auto h-14 w-14 text-red-400" aria-hidden />
          <div className="space-y-2">
            <h1 className="display text-2xl font-semibold text-foreground">
              This reset link didn’t work
            </h1>
            <p id="reset-link-error" className="leading-relaxed text-muted-foreground">
              {view.message}
            </p>
          </div>
          <AuthReturnActions
            onSecondary={goToWebsiteSignIn}
            supportingCopy="In MovieMe, choose Forgot password to request a fresh link."
          />
        </div>
      </AuthCallbackShell>
    );
  }

  if (view.status === 'success') {
    return (
      <AuthCallbackShell liveStatus="Password updated">
        <div className="space-y-6 text-center">
          <CheckCircle className="mx-auto h-14 w-14 text-emerald-400" aria-hidden />
          <div className="space-y-2">
            <h1 className="display text-2xl font-semibold text-foreground">Password updated</h1>
            <p className="leading-relaxed text-muted-foreground">
              Your new password is ready. Return to MovieMe and sign in.
            </p>
          </div>
          <AuthReturnActions onSecondary={goToWebsiteSignIn} />
        </div>
      </AuthCallbackShell>
    );
  }

  const saving = view.status === 'saving';
  const formError = view.status === 'ready' ? view.formError : null;

  return (
    <AuthCallbackShell liveStatus={saving ? 'Updating password' : 'Choose a new password'}>
      <div className="space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="display text-2xl font-semibold text-foreground sm:text-3xl">
            Choose a new password
          </h1>
          <p className="text-sm text-muted-foreground">Enter and confirm your new password below.</p>
        </div>

        {formError ? (
          <div
            id="reset-form-error"
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-red-300"
          >
            {formError}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
              New password
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" aria-hidden />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark min-h-[44px] pl-11 pr-12"
                placeholder="Enter new password"
                required
                minLength={6}
                autoComplete="new-password"
                disabled={saving}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? 'reset-form-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="btn-quiet absolute right-1.5 top-1.5"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                disabled={saving}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              Confirm password
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-muted-foreground/60" aria-hidden />
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-dark min-h-[44px] pl-11"
                placeholder="Confirm new password"
                required
                minLength={6}
                autoComplete="new-password"
                disabled={saving}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? 'reset-form-error' : undefined}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="btn-marquee min-h-[44px] w-full px-4 py-3.5 text-base"
          >
            {saving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </AuthCallbackShell>
  );
}
